import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { refLink } from "@/lib/referral";
import Navbar from "@/components/Navbar";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { MarketingResources } from "@/components/MarketingResources";
import { AffiliateProfile } from "@/components/AffiliateProfile";
import { SupportTickets } from "@/components/SupportTickets";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Building2, Hotel, Copy, Clock, CheckCircle2, ExternalLink, ShieldAlert, Ban, Handshake, MousePointerClick, Users, DollarSign, LayoutDashboard, User, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const HOTELS_IMG = "https://images.unsplash.com/photo-1610641818989-c2051b5e2cfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjByZXNvcnQlMjBwb29sfGVufDB8fHx8MTc4MzY0MzIyMXww&ixlib=rb-4.1.0&q=85";
const CONDOS_IMG = "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb25kbyUyMGludGVyaW9yfGVufDB8fHx8MTc4MzY0MzIyMnww&ixlib=rb-4.1.0&q=85";

const HOTELS_URL = "https://hotels.internationalvacations.com/v6/";
const CONDOS_URL = "https://www.condobooker.com/booknow.aspx";

const CategoryCard = ({ category, title, img, icon: Icon, url }) => (
  <motion.a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    whileHover={{ y: -6 }}
    className="group relative block text-left overflow-hidden border border-[var(--ir-border)] bg-white rounded-lg soft-shadow"
    data-testid={`category-card-${category}`}
  >
    <div className="relative h-56 overflow-hidden">
      <img src={img} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute top-4 left-4 w-11 h-11 flex items-center justify-center bg-white/90 rounded-md">
        <Icon className="w-6 h-6 text-[var(--ir-blue)]" />
      </div>
    </div>
    <div className="p-7">
      <h3 className="font-display text-3xl text-[var(--ir-text)]">{title}</h3>
      <div className="mt-4 flex items-center gap-2 text-sm text-[var(--ir-orange)] font-medium">
        <ExternalLink className="w-4 h-4" /> Open the private booking portal
      </div>
    </div>
  </motion.a>
);

const MemberDashboard = () => {
  const { user } = useAuth();
  const hasActiveCode = user?.access_code && user?.code_active;
  const [aff, setAff] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const isAffiliate = !!user?.is_affiliate;
  const requested = searchParams.get("tab") || "overview";
  // Profile/Support are affiliate-only for now; fall back to overview otherwise.
  const activeTab = isAffiliate && ["overview", "profile", "support"].includes(requested) ? requested : "overview";
  const setTab = (t) => setSearchParams(t === "overview" ? {} : { tab: t }, { replace: false });

  useEffect(() => {
    if (user?.is_affiliate) {
      api.get("/affiliate/me").then(({ data }) => setAff(data)).catch(() => {});
    }
  }, [user]);

  const copyCode = () => {
    navigator.clipboard.writeText(user.access_code);
    toast.success("PIN number copied");
  };

  const overview = (
    <>
      <div className="grid lg:grid-cols-3 gap-8">
        {/* VIP CARD */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="lg:col-span-1">
          <div className={`relative overflow-hidden p-8 h-full rounded-xl blue-card text-white ${hasActiveCode ? "code-glow" : "soft-shadow"}`} data-testid="member-vip-card">
            <div className="flex items-center justify-between">
              <span className="text-xs tracking-[0.3em] uppercase text-white/80">Member PIN Number</span>
              {hasActiveCode ? (
                <Badge className="bg-white/20 text-white border border-white/30 rounded-none" data-testid="member-status-badge"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>
              ) : user?.access_code ? (
                <Badge className="bg-rose-500 text-white border-0 rounded-none" data-testid="member-status-badge"><Ban className="w-3 h-3 mr-1" />Inactive</Badge>
              ) : (
                <Badge className="bg-[var(--ir-orange)] text-white border-0 rounded-none" data-testid="member-status-badge"><Clock className="w-3 h-3 mr-1" />Pending</Badge>
              )}
            </div>

            {hasActiveCode ? (
              <>
                <div className="mt-10 font-mono text-3xl sm:text-4xl tracking-[0.15em] text-white" data-testid="member-access-code">
                  {user.access_code}
                </div>
                <Button onClick={copyCode} className="mt-8 bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none" data-testid="copy-code-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy PIN
                </Button>
                <p className="text-white/75 text-sm mt-6 leading-relaxed">Use this 10-digit PIN number on the Hotels and Condos booking portals to unlock your members-only rates.</p>
                <div className="mt-5 flex items-start gap-2 rounded-md bg-white/10 border border-white/20 p-3" data-testid="member-pin-warning">
                  <ShieldAlert className="w-4 h-4 text-[var(--ir-orange)] shrink-0 mt-0.5" />
                  <p className="text-white/80 text-xs leading-relaxed">Do not share your card/PIN number with anyone. Any violation of this rule will invalidate your card/PIN number and cancel all reservations made using this subscription program.</p>
                </div>
              </>
            ) : (
              <div className="mt-10">
                <ShieldAlert className="w-10 h-10 text-white/90" />
                <p className="text-white/90 mt-5 leading-relaxed">
                  {user?.access_code
                    ? "Your PIN number is currently inactive. Please contact an administrator to reactivate it."
                    : "Your application is under review. Once an administrator approves your membership, your private 10-digit PIN number will appear here."}
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* CATEGORIES */}
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-8">
          <CategoryCard category="hotels" title="Hotels & Resorts" img={HOTELS_IMG} icon={Hotel} url={HOTELS_URL} />
          <CategoryCard category="condos" title="Vacation Condos" img={CONDOS_IMG} icon={Building2} url={CONDOS_URL} />
        </div>
      </div>

      {aff && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-14" data-testid="affiliate-panel">
          <div className="flex items-center gap-2 mb-6">
            <Handshake className="w-6 h-6 text-[var(--ir-orange)]" />
            <h2 className="font-display text-3xl text-[var(--ir-text)]">Your Affiliate Program</h2>
          </div>

          <div className="bg-white border border-[var(--ir-border)] rounded-xl soft-shadow p-7">
            <Label className="text-[var(--ir-muted)] text-sm">Your referral links</Label>
            <div className="mt-2 space-y-3" data-testid="affiliate-links-list">
              {(aff.links && aff.links.length ? aff.links : [{ id: "default", name: "Default referral link", link: refLink(aff.affiliate_code), is_default: true, clicks: aff.clicks, conversions: aff.approved_referrals }]).map((l) => (
                <div key={l.id} className="bg-[var(--ir-surface2)] border border-[var(--ir-border)] rounded-lg p-4" data-testid={`affiliate-link-${l.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-[var(--ir-text)]">{l.name}</span>
                    {l.is_default && <Badge className="rounded-none border bg-[var(--ir-blue)]/10 text-[var(--ir-blue)] border-[var(--ir-blue)]/30 text-[10px]">Default</Badge>}
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch gap-2">
                    <div className="flex-1 bg-white border border-[var(--ir-border)] rounded-none px-4 py-2.5 font-mono text-sm text-[var(--ir-blue)] truncate" data-testid={l.is_default ? "affiliate-referral-link" : undefined}>{l.link}</div>
                    <Button onClick={() => { navigator.clipboard.writeText(l.link); toast.success("Referral link copied"); }} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none" data-testid={l.is_default ? "affiliate-copy-link-btn" : `affiliate-copy-link-btn-${l.id}`}>
                      <Copy className="w-4 h-4 mr-2" />Copy
                    </Button>
                  </div>
                  <div className="flex gap-6 mt-2 text-xs text-[var(--ir-muted)]">
                    <span>{l.clicks ?? 0} clicks</span>
                    <span>{l.conversions ?? 0} conversions</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-7">
              {[
                { icon: MousePointerClick, label: "Clicks", value: aff.clicks },
                { icon: Users, label: "Sign-ups", value: aff.signups },
                { icon: CheckCircle2, label: "Approved", value: aff.approved_referrals },
                { icon: DollarSign, label: "Balance owed", value: `$${aff.commission_owed.toFixed(2)}` },
              ].map((s) => (
                <div key={s.label} className="border border-[var(--ir-border)] rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--ir-muted)] text-xs">{s.label}</span>
                    <s.icon className="w-4 h-4 text-[var(--ir-blue)]" />
                  </div>
                  <div className="font-display text-3xl mt-2 text-[var(--ir-text)]">{s.value}</div>
                </div>
              ))}
            </div>

            <h3 className="text-sm font-semibold text-[var(--ir-text)] mt-8 mb-3">People you've referred ({aff.referrals.length})</h3>
            <div className="border border-[var(--ir-border)] rounded-lg divide-y divide-[var(--ir-border)]">
              {aff.referrals.length === 0 ? (
                <div className="text-[var(--ir-muted)] text-sm p-4">No referrals yet — share your link to start earning.</div>
              ) : aff.referrals.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3" data-testid={`affiliate-referral-${i}`}>
                  <span className="text-[var(--ir-text)]">{r.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-[var(--ir-muted)] text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
                    <Badge className={`rounded-none border capitalize text-xs ${r.status === "approved" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-[var(--ir-orange)]/10 text-[var(--ir-orange)] border-[var(--ir-orange)]/30"}`}>{r.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {isAffiliate && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mt-14">
          <MarketingResources />
        </motion.div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[var(--ir-bg)] text-[var(--ir-text)]" data-testid="member-dashboard">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 pt-40 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-[var(--ir-orange)] tracking-[0.3em] uppercase text-xs font-semibold">Welcome back</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[var(--ir-text)]">{user?.name}</h1>
        </motion.div>

        {isAffiliate && (
          <div className="mt-8">
            <AnnouncementBanner />
          </div>
        )}

        {isAffiliate ? (
          <Tabs value={activeTab} onValueChange={setTab} className="mt-10">
            <TabsList className="bg-white border border-[var(--ir-border)] rounded-none">
              <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="member-tab-overview"><LayoutDashboard className="w-4 h-4 mr-2" />Overview</TabsTrigger>
              <TabsTrigger value="profile" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="member-tab-profile"><User className="w-4 h-4 mr-2" />My Profile</TabsTrigger>
              <TabsTrigger value="support" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="member-tab-support"><LifeBuoy className="w-4 h-4 mr-2" />Support</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="mt-8">{overview}</TabsContent>
            <TabsContent value="profile" className="mt-8"><AffiliateProfile /></TabsContent>
            <TabsContent value="support" className="mt-8"><SupportTickets /></TabsContent>
          </Tabs>
        ) : (
          <div className="mt-12">{overview}</div>
        )}
      </main>
    </div>
  );
};

export default MemberDashboard;
